import { ChevronRight, ChevronUp, Clock } from "lucide-react";
import { Image } from "../common/image";
import { RemoveIcon } from "./assets/remove-icon";
import { cn } from "@/lib/utils";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ImageCreationIcon } from "./assets/image-creation-icon";
import { VideoCreationIcon } from "./assets/video-creation-icon";
import { SelectPopover } from "../common/select-popover";
import { ModelIcon } from "./assets/model-icon";
import { DynamicPopover } from "../common/dynamic-popover";
import { MODEL_PARAMS_CONFIGS } from "./constants/ai-creation-parameter";
import { HdIcon } from "./assets/hd-icon";
import { AtomIcon } from "./assets/atom-icon";
import React from "react";
import { SmartRatioIcon } from "./assets/smart-ratio-icon";
import { AiCreationParams, AiCreationType } from "@/manager/manager/agent-user-messages-manager";
import { getModelOptionByValue, imageModelOptions, videoModelOptions } from "./constants/ai-creation-model";
import { ReferenceUploadCard } from "./ai-creation-button";
import { ImagePlusIcon } from "./assets/image-plus-icon";
import { Upload } from "../common/upload";
import ImagePreviewModal from "../common/image-preview-modal";
import { UPLOAD_ERROR_TYPE } from "@/hooks/use-upload";
import { useTranslation } from "react-i18next";
import Tooltip from "../ui/modal/Tooltip";

// 参考图片
export const ReferenceImage = ({
  urls,
  showRemove,
  showChange,
  aiCreationModel,
  aiCreationType,
  showUploadCard,
  disabledRemove,
  onUpload,
  onRemove,
  onUploadInvalid,
}: {
  urls: string[];
  showRemove?: boolean;
  showChange?: boolean;
  aiCreationModel: string;
  aiCreationType: AiCreationType;
  showUploadCard?: boolean;
  disabledRemove?: boolean;
  onUpload: (file: File | File[]) => void;
  onRemove?: (index: number) => void;
  onUploadInvalid?: (type: keyof typeof UPLOAD_ERROR_TYPE) => void;
}) => {
  const [hoverImageIndex, setHoverImageIndex] = useState(-1)
  const [isExpanded, setIsExpanded] = useState(false)
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null)
  const [openPreview, setOpenPreview] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  const hoverWrapperRef = useRef(false)
  const addButtonRef = useRef<HTMLDivElement>(null)

  const gap = 8;
  const uploadButtonWidth = aiCreationType === 'image' && showUploadCard ? 52 : 0; // 上传按钮宽度

  // 计算展开后的总宽度（包含上传按钮）
  const totalWidth = useMemo(() => {
    const count = urls.length;
    const itemWidth = 52;
    return count * itemWidth + (count - 1) * gap + (count > 0 ? gap + uploadButtonWidth : uploadButtonWidth)
  }, [urls, uploadButtonWidth])

  const handleRemove = (index: number) => {
    setDeletingIndex(index)
    // 等待删除动画完成后再移除（增加延迟确保完全过渡）
    setTimeout(() => {
      onRemove?.(index)
      setDeletingIndex(null)
      // 删除完成后，如果没有hover，自动收起；如果有hover则保持展开
      if (!hoverWrapperRef.current) {
        // setIsExpanded(false)
      }
    }, 300)
  }

  const uploadCardPosition = (urls.length - (deletingIndex !== null ? 1 : 0)) * (52 + gap);
  const uploadCardOffsetX = isExpanded ? 0 : -uploadCardPosition;

  return (
    <div
      className={cn(
        'relative group select-none',
        isExpanded && 'z-50',
      )}
      onMouseEnter={(e) => {
        // 如果 hover 的是添加按钮，不触发展开
        if (addButtonRef.current?.contains(e.target as Node)) {
          return
        }
        hoverWrapperRef.current = true
        setIsExpanded(true)
      }}
      onMouseLeave={() => {
        hoverWrapperRef.current = false
        // 如果正在删除，保持展开状态直到删除完成
        if (deletingIndex === null) {
          setIsExpanded(false)
        }
      }}
    >
      {/* 展开收起的图片栈 */}
      <div
        className="absolute top-0 left-0 h-[70px] flex transition-all duration-500 ease-in-out"
        style={{
          width: totalWidth,
          gap: `${gap}px`,
          // overflow: isExpanded ? 'visible' : 'hidden',
        }}
        onMouseEnter={() => {
          // setIsExpanded(true)
        }}
      >
        {urls?.length > 0 && urls.map((url, index) => {
          // 计算图片位置时考虑删除的影响
          const isDeletedBefore = deletingIndex !== null && index > deletingIndex;
          const adjustedIndex = isDeletedBefore ? index - 1 : index;
          const finalPosition = adjustedIndex * (52 + gap);
          const offsetX = isExpanded ? 0 : -finalPosition;

          // 删除状态下的垂直缩小动画
          const isDeleting = deletingIndex === index;
          const deleteScale = isDeleting ? 'scale(0.8)' : 'scale(1)';
          const deleteOpacity = isDeleting ? 0 : 1;

          // 只在过渡时有缩放，展开完成后都是正常大小
          const baseScale = (() => {
            if (index === 0) return 1; // 第一张不参与过渡

            if (isExpanded) {
              // 展开完成后都是1
              return 1;
            } else {
              // 收起过程中才需要过渡动画，从小到大
              const count = urls.length;
              const relativeIndex = index - 1;
              const steps = Math.max(1, count - 2);
              return 0.8 + (relativeIndex / steps) * 0.2;
            }
          })();

          // 展开收起时增加opacity效果
          const baseOpacity = (() => {
            if (index === 0) return 1; // 第一张不参与过渡

            if (isExpanded) {
              // 展开完成后都是1
              return 1;
            } else {
              // 收起过程中，从透明到不透明
              const count = urls.length;
              const relativeIndex = index - 1;
              const steps = Math.max(1, count - 2);
              return 0.4 + (relativeIndex / steps) * 0.6;
            }
          })();

          // z-index：收起时第一张最前，其他按顺序在下面；展开时也保持顺序
          const zIndex = index === 0 ? urls.length : urls.length - index;

          const scale = hoverImageIndex === index && !isDeleting ? 'scale(1)' : `scale(${baseScale})`;

          return (
            <div
              key={url}
              className={cn(
                "group/item group relative flex-shrink-0 w-[52px] h-[70px] rounded-[8px] border border-black/10 transition-all duration-500 ease-in-out bg-white origin-bottom-left cursor-pointer",
                isDeleting && "duration-300 origin-center",
              )}
              style={{
                zIndex: zIndex,
                transitionDelay: isDeleting ? '0ms' : `${index * 30}ms`,
                transform: `translateX(${offsetX}px) ${isDeleting ? deleteScale : scale}`,
                opacity: isDeleting ? deleteOpacity : baseOpacity,
              }}
              onMouseEnter={(e) => {
                !isDeleting && setHoverImageIndex(index)
                index === 0 && setIsExpanded(true)
              }}
              onMouseLeave={() => setHoverImageIndex(-1)}
            >
              <Image
                key={url}
                src={url}
                className="size-full rounded-[8px] object-cover group-hover/item:scale-110 transition-all duration-300 group-hover/item:shadow-lg"
                onClick={(e) => {
                  e.stopPropagation()
                  setPreviewIndex(index)
                  setOpenPreview(true)
                  setIsExpanded(false)
                }}
              />
              {/* 移除按钮 */}
              {!disabledRemove && (
                <div
                  className={cn(
                    "absolute top-0.5 right-0.5 cursor-pointer z-50 opacity-0 transition-all scale-0 hover:bg-black/10 rounded-full",
                    showRemove && "group-hover/item:opacity-100 group-hover/item:scale-100",
                  )}
                  onClick={(e) => { e.stopPropagation(); handleRemove(index); }}
                  onMouseEnter={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <RemoveIcon className="size-5 transition-all hover:text-black" />
                </div>
              )}
            </div>
          );
        })}
        {/* 上传卡片，跟随动画移动 */}
        {aiCreationType === 'image' && showUploadCard && (
          <div
            style={{
              transform: `translateX(${uploadCardOffsetX}px)`,
              transitionDelay: `${urls.length * 30}ms`,
            }}
            className={cn(
              "transition-all duration-500 ease-in-out flex-shrink-0 w-[52px] h-[70px] rounded-[8px]",
              showUploadCard && "opacity-100 scale-100",
            )}
          >
            <ReferenceUploadCard
              aiCreationType={aiCreationType}
              aiCreationModel={aiCreationModel}
              tooltipOffset={0}
              onUpload={onUpload}
            />
          </div>
        )}

      </div>
      {/* 添加按钮 */}
      {((aiCreationType === 'image' && urls?.length > 0) && showUploadCard) && (
        <Upload
          className={cn(
            "absolute -bottom-[75px] -right-1 z-50 cursor-pointer hover:scale-110 transition-all duration-300 active:scale-90 opacity-100",
            isExpanded && "opacity-0 scale-0"
          )}
          multiple={aiCreationType === 'image'}
          acceptedFileTypes={getModelOptionByValue(aiCreationModel)?.acceptedInputFileTypes}
          maxFileSize={getModelOptionByValue(aiCreationModel)?.maxInputFileSize || 10 * 1024 * 1024}
          minFileSize={0}
          onUpload={onUpload}
          onInvalid={(type) => onUploadInvalid(type)}
        >
          <div
            ref={addButtonRef}
          >
            <ImagePlusIcon className="size-5 transition-colors duration-300 text-[#f2f4f7] hover:text-[#d0d5dd]" />
          </div>
        </Upload>
      )}
      {/* 隐藏的堆叠图片（Hover 时显示） */}
      {urls?.length > 0 && urls.slice(1, 3).map((url, index) => (
        <Image
          key={url}
          src={url}
          className={cn(
            "size-full object-cover absolute inset-0 rounded-[8px] origin-bottom-left transition-all duration-500 ease-in-out w-[52px] h-[70px]",
            index === 0 && "rotate-[3deg] opacity-60",
            index === 1 && "rotate-[6deg] opacity-30",
            isExpanded && "z-0 opacity-0",
            !isExpanded && "pointer-events-none"
          )}
        />
      ))}
      {/* 图片预览 */}
      <ImagePreviewModal
        open={openPreview}
        imageUrls={urls}
        currentIndex={previewIndex}
        onOpenChange={(open) => {
          setOpenPreview(open)
        }}
      />
    </div>
  );
};

// 创作类型切换按钮
export const CreationTypeButton = ({
  type,
  onModeChange,
}: {
  type: AiCreationType;
  onModeChange?: (type: AiCreationType) => void;
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(type)
  const buttonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSelected(type)
  }, [type])


  const options: { value: AiCreationType; label: string; icon: ReactNode }[] = [
    {
      value: 'video',
      label: t('ai_creation.type.video_generate'),
      icon: <VideoCreationIcon className="size-5" />,
    },
    {
      value: 'image',
      label: t('ai_creation.type.image_generate'),
      icon: <ImageCreationIcon className="size-5" />,
    },
  ]

  return (
    <div className="size-full relative max-w-max" ref={buttonRef}>

      <SelectPopover
        trigger={
          <button
            className={cn(
              "rounded-[8px] bg-white cursor-pointer py-[7.5px] px-[10px]",
              "flex items-center justify-center gap-1.5 border border-[rgba(16,24,40,0.05)]",
              "hover:bg-[rgba(16,24,40,0.05)] transition-all select-none"
            )}
            onClick={() => setOpen(!open)}
          >
            {selected === 'video' && <VideoCreationIcon className="size-4" />}
            {selected === 'image' && <ImageCreationIcon className="size-4" />}
            <span className="text-xs text-[rgba(16,24,40,1)]">{selected === 'video' ? t('ai_creation.type.video_generate') : t('ai_creation.type.image_generate')}</span>
            <ChevronRight className={cn(
              "size-3 transition-transform duration-200",
              open && "-rotate-90"
            )} />
          </button>
        }
        title={t('ai_creation.creation_type')}
        className="w-[200px] h-[129px] rounded-[16px] border border-[rgba(234,236,240,1)] bg-white [box-shadow:0px_4px_6px_-2px_rgba(16,_24,_40,_0.03),_0px_12px_16px_-4px_rgba(16,_24,_40,_0.08)]"
        options={options}
        value={selected}
        onOpenChange={(open) => setOpen(open)}
        onValueChange={(val: 'video' | 'image') => {
          setSelected(val);
          onModeChange?.(val);
        }}
      />
    </div>
  );
}

// 模型选择按钮
export const ModelButton = ({
  model,
  aiCreationType,
  referenceImages,
  onModelChange,
}: {
  model: string;
  aiCreationType: AiCreationType;
  referenceImages?: string[];
  onModelChange?: (model: string) => void;
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(model);

  // 根据创作模式选择模型选项
  const modelOptions = aiCreationType === 'video'
    ? videoModelOptions()
    : imageModelOptions();

  useEffect(() => {
    setSelected(model)
  }, [model]);

  return (
    <div className="relative max-w-max">
      <SelectPopover
        title={t('ai_creation.choose_model')}
        trigger={
          <button
            className={cn(
              "min-w-[95px] max-w-[140px] rounded-[8px] bg-white cursor-pointer py-[7.5px] px-[10px]",
              "flex items-center justify-center gap-1.5 border border-[rgba(16,24,40,0.05)]",
              "hover:bg-[rgba(16,24,40,0.05)] transition-all select-none"
            )}
          >
            <ModelIcon className="size-4" />
            <span className="text-xs text-[rgba(16,24,40,1)] truncate">
              {modelOptions.find(o => o.value === selected)?.label}
            </span>
            <ChevronRight className={cn("size-3 transition-transform", open && "-rotate-90")} />
          </button>
        }
        className="min-w-[256px] !max-h-max rounded-[16px] border border-[rgba(234,236,240,1)] bg-white [box-shadow:0px_4px_6px_-2px_rgba(16,_24,_40,_0.03),_0px_12px_16px_-4px_rgba(16,_24,_40,_0.08)]"
        itemClassName="py-[10px]"
        options={modelOptions}
        value={selected}
        onOpenChange={(open) => setOpen(open)}
        onValueChange={(val) => {
          setSelected(val);
          onModelChange?.(val);
        }}
      />
    </div>
  );
};

interface ParameterButtonProps {
  aiCreationType: AiCreationType;
  aiCreationModel: string;
  params: AiCreationParams;
  hasReferenceImage?: boolean;
  onParamsChange?: (params: AiCreationParams) => void;
}

/**
 * 参数选择按钮
 * 【视频生成】
 * Sora2：比例、时长
 * Sora2-pro：比例、分辨率、时长
 * 图片生成：比例、分辨率、输出数量
 * @param param0 
 * @returns 
 */
export const ParameterButton = ({
  params = {},
  aiCreationType,
  aiCreationModel,
  hasReferenceImage,
  onParamsChange,
}: ParameterButtonProps) => {
  const { t } = useTranslation()
  const [state, setState] = useState({ ...params });

  useEffect(() => {
    setState({ ...params });
  }, [params]);

  // 定义每个字段的具体配置模板
  const FIELD_TEMPLATES = {
    aspectRatio: {
      id: 'aspectRatio',
      key: 'aspectRatio',
      title: t('ai_creation.parameter.aspect_ratio'),
      smartTitle: t('ai_creation.parameter.smart_ratio'),
      smartIcon: <SmartRatioIcon className="size-4" />
    },
    imageSize: {
      id: 'imageSize',
      key: 'imageSize',
      title: t('ai_creation.parameter.resolution'),
      icon: HdIcon
    },
    duration: {
      id: 'duration',
      key: 'duration',
      title: t('ai_creation.parameter.duration'),
      icon: Clock
    },
    count: {
      id: 'count',
      key: 'count',
      title: t('ai_creation.parameter.output_count'),
      icon: AtomIcon,
      layout: 'horizontal' as const
    },
  };

  // 计算当前模型应展示的配置项
  const activeConfig = useMemo(() => {
    const modelConfig = MODEL_PARAMS_CONFIGS[aiCreationModel];
    if (!modelConfig) return [];

    return modelConfig.fields.map((fieldId: string) => {
      const template = FIELD_TEMPLATES[fieldId as keyof typeof FIELD_TEMPLATES];
      const options = modelConfig.options[fieldId];

      // 仅在配置明确要求且有参考图时禁用比例
      const isDisabled = fieldId === 'aspectRatio' && modelConfig.disableRatioByRef && hasReferenceImage;

      return {
        ...template,
        options,
        value: state[template.key as keyof typeof state]?.toString(),
        disabled: isDisabled,
        disabledText: isDisabled ? t('ai_creation.smart_ratio_disabled_text') : '',
        onChange: (val: string) => {
          const newState = { ...state, [template.key]: val };
          setState(newState);
          onParamsChange?.(newState);
        },
      };
    });
  }, [aiCreationModel, hasReferenceImage, state, onParamsChange]);

  return (
    <DynamicPopover
      side="top"
      align="center"
      sideOffset={6}
      triggerHidden={false}
      className="rounded-[16px] border border-[rgba(234,236,240,1)] bg-white shadow-lg min-w-[370px]"
      trigger={
        <button className="py-2 px-[10px] min-w-max border border-[rgba(16,24,40,0.05)] flex items-center rounded-[8px] hover:bg-[rgba(16,24,40,0.05)] transition-all select-none">
          {activeConfig.map((item, index) => {
            const currentOption = item.options?.find(o => o.value === item.value);
            const Icon = currentOption?.icon || item.icon;

            return (
              <React.Fragment key={item.id}>
                <div className="flex items-center gap-1.5 shrink-0">
                  {/* 禁用状态（参考图占位）逻辑 */}
                  {item.disabled ? (
                    <>
                      {item.smartIcon}
                      <span className="text-xs text-[rgba(16,24,40,1)]">{item.smartTitle}</span>
                    </>
                  ) : (
                    <>
                      {Icon && <Icon className="size-4 text-[rgba(16,24,40,1)]" />}
                      <span className="text-xs text-[rgba(16,24,40,1)]">
                        {currentOption?.label || '-'}
                      </span>
                    </>
                  )}
                </div>
                {index < activeConfig.length - 1 && (
                  <div className="w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[7.5px]" />
                )}
              </React.Fragment>
            );
          })}
        </button>
      }
    >
      <div className="p-4 flex flex-col gap-4">
        {activeConfig.map(item => (
          <OptionsCard
            key={item.id}
            title={item.title}
            options={item.options}
            value={item.value}
            disabled={item.disabled}
            layout={item.layout}
            onChange={item.onChange}
            disabledText={item.disabledText}
          />
        ))}
      </div>
    </DynamicPopover>
  );
};

/**
 * 参数-通用配置卡片容器
 */
const OptionsCard = ({
  title,
  options,
  value,
  layout = 'auto',
  disabled,
  onChange,
  disabledText
}: {
  title: string;
  options: { label: string; value: string }[];
  value: string;
  disabled?: boolean;
  layout?: 'horizontal' | 'vertical' | 'auto';
  onChange: (val: string) => void;
  disabledText?: string;
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const count = options.length;
  const isSmallLayout = layout === 'horizontal' || (layout === 'auto' && count <= 3);

  // 只有在非强制水平且数量大于6时才显示“展开/收起”
  const showExpandTrigger = layout !== 'horizontal' && count > 6;
  const isFixedMode = count >= 6;

  const displayOptions = (isExpanded || count <= 6 || layout === 'horizontal')
    ? options
    : options.slice(0, 6);


  useEffect(() => {
    // 当选中项超过前6个时，默认展开
    if (value) {
      const index = options.findIndex((item) => item.value === value);
      if (index > 6) {
        setIsExpanded(true);
      }
    }
  }, [options, value]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[rgba(152,162,179,1)] text-sm">{title}</div>
        {showExpandTrigger && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-[rgba(102,112,133,1)] hover:text-[rgba(16,24,40,1)] transition-colors"
          >
            <span className="text-xs text-[rgba(102,112,133,1)]">{isExpanded ? t("ai_creation.parameter.collapse") : t("ai_creation.parameter.expend")}</span>
            <ChevronUp className={cn(
              "size-4 transition-transform duration-300",
              !isExpanded && "rotate-180"
            )} />
          </button>
        )}
      </div>

      <div className="bg-[rgba(16,24,40,0.05)] rounded-[8px] p-[2px]">
        <div className={cn(
          "grid gap-0 transition-all duration-300",
          isFixedMode ? "grid-cols-6" : `grid-cols-${count}`
        )}>
          {displayOptions.map((opt) => (
            <OptionsItem
              key={opt.value}
              opt={opt}
              isActive={value === opt.value}
              isSmall={isSmallLayout}
              isFixed={isFixedMode}
              disabled={disabled}
              disabledTooltipText={disabledText}
              onClick={() => !disabled && onChange(opt.value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * 参数-单个网格选项
 */
const OptionsItem = ({
  opt,
  isActive,
  isSmall,
  isFixed,
  onClick,
  disabled = false,
  disabledTooltipText = ''
}) => {
  const Icon = opt.icon;
  return (
    <Tooltip
      side='top'
      align='center'
      disabled={!disabled}
      trigger={
        <div
          onClick={disabled ? undefined : onClick}
          className={cn(
            "flex cursor-pointer transition-all rounded-[6px] items-center justify-center select-none",
            isSmall ? "h-[36px] flex-row gap-1.5" : "h-[68px] flex-col",
            isFixed ? "w-[54px]" : "w-full",
            (isActive && !disabled) ? "bg-white shadow-sm" : "hover:bg-[rgba(16,24,40,0.02)]",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          {Icon && (
            <Icon className={cn(
              "size-5 transition-colors",
              !isSmall && "mb-1.5",
              isActive ? "text-[rgba(16,24,40,1)]" : "text-[rgba(102,112,133,1)]"
            )} />
          )}
          <span className={cn(
            "text-sm transition-colors whitespace-nowrap",
            isActive ? "text-[rgba(16,24,40,1)]" : "text-[rgba(102,112,133,1)]"
          )}>
            {opt.label}
          </span>
        </div>
      }
      content={
        disabledTooltipText ? (
          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px] min-w-max'>
            <span className={cn('text-[rgba(255,255,255,1)] text-sm font-medium')}>
              {disabledTooltipText}
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-[rgba(16,24,40,1)]"></div>
          </div>
        ) : null
      }
    />
  );
};